import {
    AibackReportAspectRowEntity,
    AibackReportPdfPayloadEntity,
    AibackReportPdfTopItemEntity,
    AibackReportPdfTreemapItemEntity,
    AibackReportRatingTimelinePointEntity,
    AibackReportResponseTimeTimelinePointEntity,
    AibackReportSummaryEntity,
} from '@/entities/aiback-report.entity';
import { normalizeRatingBuckets } from '@/domain/reporting/reporting-shared.collections';

export type ReportModalSummaryData = {
    averageResponseTimeMinutes: number | null;
    answeredReviewsCount: number;
    staleDaysCount: number;
    freshRollupDaysCount: number;
    dirtyRollupDaysCount: number;
    generatedFromScratchDaysCount: number;
    firstReviewDate: string | null;
    periodAverageRating: number | null;
    averageRatingTimeline: AibackReportRatingTimelinePointEntity[];
    averageResponseTimeTimeline: AibackReportResponseTimeTimelinePointEntity[];
    aspectRows: AibackReportAspectRowEntity[];
    positiveHighlights: string[];
    positiveHighlightsHiddenCount: number;
    negativeHighlights: string[];
    negativeHighlightsHiddenCount: number;
    neutralHighlights: string[];
    neutralHighlightsHiddenCount: number;
    periodTotalReviews: number;
    periodTotalAnswers: number;
    periodTotalAiAnalyses: number;
    periodChannelsCount: number;
    periodProductsCount: number;
    periodBrandsCount: number;
    periodTopChannels: AibackReportPdfTopItemEntity[];
    periodTopProducts: AibackReportPdfTopItemEntity[];
    periodTopBrands: AibackReportPdfTopItemEntity[];
    periodRatingBuckets: Array<{
        stars: number;
        value: number;
    }>;
    periodTreemapItems: AibackReportPdfTreemapItemEntity[];
};

const toNullableFiniteNumber = (value: unknown): number | null => {
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
};

const toRoundedPositiveInteger = (value: unknown): number => {
    return typeof value === 'number' && value > 0 ? Math.round(value) : 0;
};

const toNullableIsoDate = (value: unknown): string | null => {
    return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : null;
};

export const createEmptyReportModalSummaryData = (): ReportModalSummaryData => ({
    averageResponseTimeMinutes: null,
    answeredReviewsCount: 0,
    staleDaysCount: 0,
    freshRollupDaysCount: 0,
    dirtyRollupDaysCount: 0,
    generatedFromScratchDaysCount: 0,
    firstReviewDate: null,
    periodAverageRating: null,
    averageRatingTimeline: [],
    averageResponseTimeTimeline: [],
    aspectRows: [],
    positiveHighlights: [],
    positiveHighlightsHiddenCount: 0,
    negativeHighlights: [],
    negativeHighlightsHiddenCount: 0,
    neutralHighlights: [],
    neutralHighlightsHiddenCount: 0,
    periodTotalReviews: 0,
    periodTotalAnswers: 0,
    periodTotalAiAnalyses: 0,
    periodChannelsCount: 0,
    periodProductsCount: 0,
    periodBrandsCount: 0,
    periodTopChannels: [],
    periodTopProducts: [],
    periodTopBrands: [],
    periodRatingBuckets: normalizeRatingBuckets(),
    periodTreemapItems: [],
});

export const normalizeReportModalSummaryData = (
    summary: Partial<AibackReportSummaryEntity> | null | undefined,
): ReportModalSummaryData => {
    if (!summary) {
        return createEmptyReportModalSummaryData();
    }

    return {
        averageResponseTimeMinutes: toNullableFiniteNumber(summary.averageResponseTimeMinutes),
        answeredReviewsCount: toRoundedPositiveInteger(summary.answeredReviewsCount),
        staleDaysCount: toRoundedPositiveInteger(summary.staleDaysCount),
        freshRollupDaysCount: toRoundedPositiveInteger(summary.freshRollupDaysCount),
        dirtyRollupDaysCount: toRoundedPositiveInteger(summary.dirtyRollupDaysCount),
        generatedFromScratchDaysCount: toRoundedPositiveInteger(summary.generatedFromScratchDaysCount),
        firstReviewDate: toNullableIsoDate(summary.firstReviewDate),
        periodAverageRating: toNullableFiniteNumber(summary.overallAverageRating),
        averageRatingTimeline: Array.isArray(summary.averageRatingTimeline) ? summary.averageRatingTimeline : [],
        averageResponseTimeTimeline: Array.isArray(summary.averageResponseTimeTimeline)
            ? summary.averageResponseTimeTimeline
            : [],
        aspectRows: Array.isArray(summary.aspectRows) ? summary.aspectRows : [],
        positiveHighlights: Array.isArray(summary.positiveHighlights)
            ? summary.positiveHighlights
                  .filter((item): item is string => typeof item === 'string')
                  .map((item) => item.trim())
                  .filter(Boolean)
            : [],
        positiveHighlightsHiddenCount: toRoundedPositiveInteger(summary.positiveHighlightsHiddenCount),
        negativeHighlights: Array.isArray(summary.negativeHighlights)
            ? summary.negativeHighlights
                  .filter((item): item is string => typeof item === 'string')
                  .map((item) => item.trim())
                  .filter(Boolean)
            : [],
        negativeHighlightsHiddenCount: toRoundedPositiveInteger(summary.negativeHighlightsHiddenCount),
        neutralHighlights: Array.isArray(summary.neutralHighlights)
            ? summary.neutralHighlights
                  .filter((item): item is string => typeof item === 'string')
                  .map((item) => item.trim())
                  .filter(Boolean)
            : [],
        neutralHighlightsHiddenCount: toRoundedPositiveInteger(summary.neutralHighlightsHiddenCount),
        periodTotalReviews: toRoundedPositiveInteger(summary.totalReviews),
        periodTotalAnswers: toRoundedPositiveInteger(summary.totalAnswers),
        periodTotalAiAnalyses: toRoundedPositiveInteger(summary.totalAiAnalyses),
        periodChannelsCount: toRoundedPositiveInteger(summary.channelsCount),
        periodProductsCount: toRoundedPositiveInteger(summary.productsCount),
        periodBrandsCount: toRoundedPositiveInteger(summary.brandsCount),
        periodTopChannels: Array.isArray(summary.topChannels) ? summary.topChannels : [],
        periodTopProducts: Array.isArray(summary.topProducts) ? summary.topProducts : [],
        periodTopBrands: Array.isArray(summary.topBrands) ? summary.topBrands : [],
        periodRatingBuckets: normalizeRatingBuckets(Array.isArray(summary.ratingBuckets) ? summary.ratingBuckets : []),
        periodTreemapItems: Array.isArray(summary.treemapItems)
            ? summary.treemapItems.map((item) => ({
                  id: item.id,
                  label: item.label,
                  value: Math.max(0, Math.round(item.value || 0)),
                  rating: typeof item.rating === 'number' && Number.isFinite(item.rating) ? item.rating : null,
              }))
            : [],
    };
};

export const buildReportModalPdfPayload = (
    input: Omit<AibackReportPdfPayloadEntity, 'companyName' | 'periodFrom' | 'periodTo' | 'generatedAt'> & {
        companyName?: string | null;
        periodFrom?: string | null;
        periodTo?: string | null;
        generatedAt?: string;
    },
): AibackReportPdfPayloadEntity => ({
    companyName: input.companyName || '—',
    periodFrom: input.periodFrom || '—',
    periodTo: input.periodTo || '—',
    generatedAt: input.generatedAt || new Date().toISOString(),
    selectedPlatformLabels: input.selectedPlatformLabels.filter((item) => item.trim()),
    selectedProductLabels: input.selectedProductLabels.filter((item) => item.trim()),
    selectedBrandLabels: input.selectedBrandLabels.filter((item) => item.trim()),
    totalReviews: input.totalReviews,
    totalAnswers: input.totalAnswers,
    answersShare: input.answersShare,
    averageResponseTimeMinutes: input.averageResponseTimeMinutes,
    answeredReviewsCount: input.answeredReviewsCount,
    totalAiAnalyses: input.totalAiAnalyses,
    companyAverageRating: input.companyAverageRating,
    channelsCount: input.channelsCount,
    productsCount: input.productsCount,
    brandsCount: input.brandsCount,
    topChannels: input.topChannels.map((channel) => ({
        name: channel.name,
        reviewCount: channel.reviewCount,
        averageRating: channel.averageRating,
    })),
    topProducts: input.topProducts.map((product) => ({
        name: product.name,
        reviewCount: product.reviewCount,
        averageRating: product.averageRating,
    })),
    topBrands: input.topBrands.map((brand) => ({
        name: brand.name,
        reviewCount: brand.reviewCount,
        averageRating: brand.averageRating,
    })),
    ratingBuckets: input.ratingBuckets,
    averageRatingTimeline: input.averageRatingTimeline,
    averageResponseTimeTimeline: input.averageResponseTimeTimeline,
    treemapItems: input.treemapItems,
    aspectRows: input.aspectRows,
    positiveHighlights: input.positiveHighlights.filter((item) => item.trim()),
    positiveHighlightsHiddenCount: toRoundedPositiveInteger(input.positiveHighlightsHiddenCount),
    negativeHighlights: input.negativeHighlights.filter((item) => item.trim()),
    negativeHighlightsHiddenCount: toRoundedPositiveInteger(input.negativeHighlightsHiddenCount),
    neutralHighlights: (input.neutralHighlights || []).filter((item) => item.trim()),
    neutralHighlightsHiddenCount: toRoundedPositiveInteger(input.neutralHighlightsHiddenCount),
});
